/**
 * Finance is encrypted here, in the browser, and nowhere else.
 *
 * One random data key (AES-256-GCM) seals every finance record. It never leaves
 * this page in the clear: the server holds only copies of it locked in ways it
 * cannot open -
 *
 * - **to a person**, with RSA-OAEP: each person who may read finance has a key
 *   pair made here when they set their finance password. The private half is
 *   sealed with a key derived from that password (PBKDF2, 600,000 rounds), the
 *   public half is stored as it is, and anybody who already has the data key
 *   can lock a copy to it without learning the password;
 * - **to the recovery code**, printed once, with the same derivation.
 *
 * Everything here is Web Crypto, built into the browser. No library, nothing
 * fetched, so there is no third party in the path of the key.
 */

const ITERATIONS = 600_000
const enc = new TextEncoder()
const dec = new TextDecoder()

export type Sealed = { nonce: string; data: string }

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let text = ''
  for (let i = 0; i < view.length; i += 0x8000) text += String.fromCharCode(...view.subarray(i, i + 0x8000))

  return btoa(text)
}

export function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const raw = atob(text)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)

  return bytes
}

export function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(new ArrayBuffer(length)))
}

/** A key from a password or recovery code: slow on purpose, so a stolen key row is expensive to guess at. */
export async function deriveKey(secret: string, salt: Uint8Array<ArrayBuffer>, iterations = ITERATIONS): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', enc.encode(secret.normalize('NFKC')), 'PBKDF2', false, ['deriveKey'])

  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
}

export async function sealBytes(key: CryptoKey, bytes: Uint8Array<ArrayBuffer>, context: string): Promise<Sealed> {
  const nonce = randomBytes(12)
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: enc.encode(context) }, key, bytes)

  return { nonce: toBase64(nonce), data: toBase64(data) }
}

export async function openBytes(key: CryptoKey, sealed: Sealed, context: string): Promise<Uint8Array<ArrayBuffer>> {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64(sealed.nonce), additionalData: enc.encode(context) }, key, fromBase64(sealed.data))

  return new Uint8Array(plain)
}

/**
 * Seals a record against where it lives - its kind and id - so a client's
 * sealed data copied into an invoice's row fails to open rather than being
 * read as an invoice.
 */
export async function seal(key: CryptoKey, value: unknown, context: string): Promise<Sealed> {
  return sealBytes(key, enc.encode(JSON.stringify(value)), context)
}

export async function open<T>(key: CryptoKey, sealed: Sealed, context: string): Promise<T> {
  return JSON.parse(dec.decode(await openBytes(key, sealed, context))) as T
}

export async function newDataKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'])
}

export async function exportDataKey(key: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', key))
}

export async function importDataKey(raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt'])
}

/** A person's key pair: the public half is published, the private half sealed with their password. */
export async function newKeyPair(password: string): Promise<{ publicKey: string; privateKey: CryptoKey; wrappedPrivateKey: Sealed; salt: string; iterations: number }> {
  const pair = await crypto.subtle.generateKey({ name: 'RSA-OAEP', modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' }, true, ['encrypt', 'decrypt'])
  const salt = randomBytes(16)
  const kek = await deriveKey(password, salt)
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey))

  return {
    publicKey: toBase64(await crypto.subtle.exportKey('spki', pair.publicKey)),
    privateKey: pair.privateKey,
    wrappedPrivateKey: await sealBytes(kek, pkcs8, 'finance-private-key'),
    salt: toBase64(salt),
    iterations: ITERATIONS,
  }
}

export async function unlockPrivateKey(password: string, wrapped: Sealed, salt: string, iterations: number): Promise<CryptoKey> {
  const kek = await deriveKey(password, fromBase64(salt), iterations)
  const pkcs8 = await openBytes(kek, wrapped, 'finance-private-key')

  return crypto.subtle.importKey('pkcs8', pkcs8, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['decrypt'])
}

/** A copy of the data key that only the holder of this public key can open. */
export async function lockDataKeyTo(dataKey: CryptoKey, publicKey: string): Promise<string> {
  const key = await crypto.subtle.importKey('spki', fromBase64(publicKey), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt'])

  return toBase64(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, await exportDataKey(dataKey)))
}

export async function unlockDataKey(privateKey: CryptoKey, wrapped: string): Promise<CryptoKey> {
  return importDataKey(new Uint8Array(await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, fromBase64(wrapped))))
}

/* ---- The recovery code -------------------------------------------------------------- */

// Crockford's base32: no I, L, O or U, so it survives being read off paper.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** 40 characters in groups of five: 200 bits, printable and typeable. */
export function newRecoveryCode(): string {
  const bytes = randomBytes(25)
  let bits = 0
  let value = 0
  let out = ''

  for (const byte of bytes) {
    value = (value << 8) | byte
    bits += 8
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }

  return out.match(/.{5}/g)!.join('-')
}

/** What was typed, the way it was printed: case, spaces, dashes and the look-alike letters forgiven. */
export function normaliseRecoveryCode(typed: string): string {
  const clean = typed.toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/[IL]/g, '1').replace(/O/g, '0')

  return (clean.match(/.{1,5}/g) ?? []).join('-')
}

export async function sealDataKeyWithCode(dataKey: CryptoKey, code: string): Promise<{ wrapped: Sealed; salt: string; iterations: number }> {
  const salt = randomBytes(16)
  const kek = await deriveKey(normaliseRecoveryCode(code), salt)

  return { wrapped: await sealBytes(kek, await exportDataKey(dataKey), 'finance-recovery'), salt: toBase64(salt), iterations: ITERATIONS }
}

export async function openDataKeyWithCode(code: string, wrapped: Sealed, salt: string, iterations: number): Promise<CryptoKey> {
  const kek = await deriveKey(normaliseRecoveryCode(code), fromBase64(salt), iterations)

  return importDataKey(await openBytes(kek, wrapped, 'finance-recovery'))
}

/* ---- Fingerprints ------------------------------------------------------------------- */

/**
 * A keyed hash for what the server must keep unique without reading: an
 * invoice number, a GST month. Derived from the data key, so only somebody
 * who can read finance can make one, and equal values give equal hashes.
 */
export async function fingerprintKey(dataKey: CryptoKey): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', await exportDataKey(dataKey), 'HKDF', false, ['deriveKey'])

  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('medulla-finance'), info: enc.encode('fingerprint') },
    base,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    false,
    ['sign'],
  )
}

export async function fingerprint(key: CryptoKey, value: string): Promise<string> {
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(value)))

  return [...mac].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/* ---- A suggested password ----------------------------------------------------------- */

/** Five groups of five from the same alphabet: 125 random bits, which nobody will guess and anybody can type. */
export function suggestPassword(): string {
  const bytes = randomBytes(25)

  return [...bytes].map((byte) => ALPHABET[byte & 31]).join('').match(/.{5}/g)!.join('-')
}
